import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import {
  PageHero,
  PageHeroShell,
  PageHeroCopy,
  PageContainer,
  PageTag,
  PageTitle,
  PageSubtitle,
  PageLeadCard,
  LeadLabel,
  LeadTitle,
  LeadText,
  HeroMetrics,
  HeroMetric,
  ServicesDetailSection,
  DetailGrid,
  DetailCard,
  DetailVisual,
  DetailContent,
  DetailTag,
  DetailTitle,
  DetailDesc,
  FeatureList,
  FeatureItem,
} from './ServicesPage.styles';

const detailsMeta = [
  {
    key: 's0',
    emoji: '🌐',
    bg: 'linear-gradient(135deg, #1a2431, #28374a)',
    tagColor: '#d2a354',
    features: ['React & Next.js', 'TypeScript', 'Responsive', 'SEO On-page', 'Core Web Vitals', 'UI Animations', 'CMS integration', 'Hosting & deploy'],
  },
  {
    key: 's1',
    emoji: '📲',
    bg: 'linear-gradient(135deg, #18222d, #263546)',
    tagColor: '#b6925b',
    features: ['Offline mode', 'Service Workers', 'Push Notifications', 'Installable', 'App Shell', 'Cache API', 'Background Sync', 'Web Manifest'],
    reverse: true,
  },
  {
    key: 's2',
    emoji: '📱',
    bg: 'linear-gradient(135deg, #221b12, #3a2c1e)',
    tagColor: '#e6b866',
    features: ['iOS & Android', 'Expo SDK', 'Native APIs', 'OTA Updates', 'Push Notifications', 'Camera & GPS', 'Offline First', 'App Store deploy'],
  },
  {
    key: 's3',
    emoji: '⚡',
    bg: 'linear-gradient(135deg, #141c24, #223243)',
    tagColor: '#9fb0c1',
    features: ['React + TypeScript', 'REST & GraphQL', 'Auth & Role-based', 'Dashboards', 'CRUD systems', 'Tables & filters', 'Data export', 'CI/CD'],
    reverse: true,
  },
];

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();

  const heroMetrics = [
    { label: t('servicesPage.heroMetric0Label'), value: t('servicesPage.heroMetric0Value') },
    { label: t('servicesPage.heroMetric1Label'), value: t('servicesPage.heroMetric1Value') },
    { label: t('servicesPage.heroMetric2Label'), value: t('servicesPage.heroMetric2Value') },
  ];

  return (
    <>
      <PageHero>
        <PageContainer>
          <PageHeroShell>
            <PageHeroCopy>
              <PageTag>{t('servicesPage.tag')}</PageTag>
              <PageTitle>
                {t('servicesPage.title')} <span>{t('servicesPage.titleGradient')}</span>
              </PageTitle>
              <PageSubtitle>{t('servicesPage.subtitle')}</PageSubtitle>
            </PageHeroCopy>

            <PageLeadCard>
              <LeadLabel>{t('servicesPage.leadLabel')}</LeadLabel>
              <LeadTitle>{t('servicesPage.leadTitle')}</LeadTitle>
              <LeadText>{t('servicesPage.leadText')}</LeadText>
              <HeroMetrics>
                {heroMetrics.map((metric) => (
                  <HeroMetric key={metric.label}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                  </HeroMetric>
                ))}
              </HeroMetrics>
            </PageLeadCard>
          </PageHeroShell>
        </PageContainer>
      </PageHero>

      <ServicesDetailSection>
        <PageContainer>
          <DetailGrid>
            {detailsMeta.map((d) => (
              <DetailCard key={d.key} $reverse={d.reverse}>
                <DetailVisual $bg={d.bg}>{d.emoji}</DetailVisual>
                <DetailContent>
                  <DetailTag $color={d.tagColor}>{t(`servicesPage.${d.key}.tag`)}</DetailTag>
                  <DetailTitle>{t(`servicesPage.${d.key}.title`)}</DetailTitle>
                  <DetailDesc>{t(`servicesPage.${d.key}.desc`)}</DetailDesc>
                  <FeatureList>
                    {d.features.map((f) => (
                      <FeatureItem key={f}>
                        <FaCheck size={10} />
                        {f}
                    </FeatureItem>
                  ))}
                </FeatureList>
              </DetailContent>
            </DetailCard>
          ))}
        </DetailGrid>
      </PageContainer>
    </ServicesDetailSection>
    </>
  );
};

export default ServicesPage;
